import React, { useState, useRef } from 'react';
import { Upload, Download, FileSpreadsheet, AlertCircle, CheckCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import * as XLSX from 'xlsx';

interface ValidationError {
  row: number;
  field: string;
  message: string;
}

interface ImportedContact {
  nome: string;
  email: string;
  phone: string;
  tags: string;
  nome_do_agente?: string;
}

const normalizePhone = (phone: string): string => {
  let cleaned = phone.replace(/\D/g, '');

  if (cleaned.startsWith('55')) {
    cleaned = cleaned.substring(2);
  }

  if (cleaned.length === 11 || cleaned.length === 10) {
    return cleaned;
  }

  return cleaned;
};

const generatePhoneVariations = (phone: string): string[] => {
  const normalized = normalizePhone(phone);
  const variations: string[] = [];

  if (normalized.length === 11) {
    variations.push(`${normalized}@s.whatsapp.net`);
    variations.push(`${normalized}@c.us`);
    const without9 = normalized.substring(0, 2) + normalized.substring(3);
    variations.push(`${without9}@s.whatsapp.net`);
    variations.push(`${without9}@c.us`);
  } else if (normalized.length === 10) {
    variations.push(`${normalized}@s.whatsapp.net`);
    variations.push(`${normalized}@c.us`);
    const with9 = normalized.substring(0, 2) + '9' + normalized.substring(2);
    variations.push(`${with9}@s.whatsapp.net`);
    variations.push(`${with9}@c.us`);
  } else {
    variations.push(`${normalized}@s.whatsapp.net`);
    variations.push(`${normalized}@c.us`);
  }

  return variations;
};

const ImportarContatos: React.FC = () => {
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [validating, setValidating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [importSuccess, setImportSuccess] = useState(false);
  const [importedCount, setImportedCount] = useState(0);
  const [previewData, setPreviewData] = useState<ImportedContact[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
      alert('Por favor, selecione um arquivo Excel (.xlsx ou .xls)');
      return;
    }

    setFile(selectedFile);
    setValidationErrors([]);
    setImportSuccess(false);
    setPreviewData([]);

    await validateFile(selectedFile);
  };

  const validateFile = async (file: File) => {
    setValidating(true);
    const errors: ValidationError[] = [];
    const preview: ImportedContact[] = [];

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

      if (jsonData.length < 2) {
        errors.push({
          row: 0,
          field: 'geral',
          message: 'Arquivo vazio ou sem dados'
        });
        setValidationErrors(errors);
        setValidating(false);
        return;
      }

      const headers = jsonData[0];
      const requiredHeaders = ['nome', 'email', 'phone', 'tags'];

      const normalizedHeaders = headers.map((h: string) =>
        h?.toString().toLowerCase().trim()
      );

      for (const required of requiredHeaders) {
        if (!normalizedHeaders.includes(required)) {
          errors.push({
            row: 0,
            field: required,
            message: `Coluna "${required}" não encontrada no arquivo`
          });
        }
      }

      if (errors.length > 0) {
        setValidationErrors(errors);
        setValidating(false);
        return;
      }

      const nomeIndex = normalizedHeaders.indexOf('nome');
      const emailIndex = normalizedHeaders.indexOf('email');
      const phoneIndex = normalizedHeaders.indexOf('phone');
      const tagsIndex = normalizedHeaders.indexOf('tags');
      const nomeDoAgenteIndex = normalizedHeaders.indexOf('nome_do_agente');

      for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i];

        if (!row || row.length === 0 || !row.some(cell => cell)) {
          continue;
        }

        const nome = row[nomeIndex]?.toString().trim() || '';
        const email = row[emailIndex]?.toString().trim() || '';
        const phone = row[phoneIndex]?.toString().trim() || '';
        const tags = row[tagsIndex]?.toString().trim() || '';
        const nomeDoAgente = nomeDoAgenteIndex >= 0 ? (row[nomeDoAgenteIndex]?.toString().trim() || '') : '';

        if (!nome) {
          errors.push({
            row: i + 1,
            field: 'nome',
            message: 'Nome é obrigatório'
          });
        }

        if (!phone) {
          errors.push({
            row: i + 1,
            field: 'phone',
            message: 'Telefone é obrigatório'
          });
        } else {
          const cleanPhone = phone.replace(/\D/g, '');
          if (cleanPhone.length < 10) {
            errors.push({
              row: i + 1,
              field: 'phone',
              message: 'Telefone inválido (mínimo 10 dígitos)'
            });
          }
        }

        if (email && !email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
          errors.push({
            row: i + 1,
            field: 'email',
            message: 'E-mail inválido'
          });
        }

        if (errors.filter(e => e.row === i + 1).length === 0) {
          preview.push({
            nome,
            email,
            phone,
            tags,
            nome_do_agente: nomeDoAgente
          });
        }
      }

      setValidationErrors(errors);
      setPreviewData(preview.slice(0, 5));

    } catch (error) {
      console.error('Erro ao ler arquivo:', error);
      errors.push({
        row: 0,
        field: 'geral',
        message: 'Erro ao processar arquivo. Verifique o formato.'
      });
      setValidationErrors(errors);
    } finally {
      setValidating(false);
    }
  };

  const handleImport = async () => {
    if (!file || !user?.account_id || validationErrors.length > 0) {
      return;
    }

    setImporting(true);

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

      const headers = jsonData[0].map((h: string) => h?.toString().toLowerCase().trim());
      const nomeIndex = headers.indexOf('nome');
      const emailIndex = headers.indexOf('email');
      const phoneIndex = headers.indexOf('phone');
      const tagsIndex = headers.indexOf('tags');
      const nomeDoAgenteIndex = headers.indexOf('nome_do_agente');

      if (!supabase) {
        alert('Erro ao conectar com o banco de dados');
        setImporting(false);
        return;
      }

      const { data: assistantsData } = await supabase
        .from('account_assistants')
        .select('id, name')
        .eq('account_id', user.account_id);

      const assistantMap = new Map<string, string>();
      if (assistantsData) {
        for (const assistant of assistantsData) {
          const normalizedName = assistant.name.toLowerCase().trim();
          assistantMap.set(normalizedName, assistant.id);
        }
      }

      const allPhoneVariations: string[] = [];
      const phoneToRowData = new Map();

      for (let i = 1; i < jsonData.length; i++) {
        const row = jsonData[i];

        if (!row || row.length === 0 || !row.some(cell => cell)) {
          continue;
        }

        const nome = row[nomeIndex]?.toString().trim();
        const email = row[emailIndex]?.toString().trim() || null;
        const phone = row[phoneIndex]?.toString().trim();
        const tagsStr = row[tagsIndex]?.toString().trim() || '';
        const nomeDoAgente = nomeDoAgenteIndex >= 0 ? (row[nomeDoAgenteIndex]?.toString().trim() || '') : '';

        if (!nome || !phone) continue;

        const cleanPhone = phone.replace(/\D/g, '');
        if (cleanPhone.length < 10) continue;

        const variations = generatePhoneVariations(cleanPhone);
        allPhoneVariations.push(...variations);

        const tagsList = tagsStr
          .split(',')
          .map((t: string) => t.trim())
          .filter((t: string) => t.length > 0);

        let assistantId: string | null = null;
        if (nomeDoAgente) {
          const normalizedAgentName = nomeDoAgente.toLowerCase().trim();
          assistantId = assistantMap.get(normalizedAgentName) || null;
        }

        phoneToRowData.set(cleanPhone, {
          nome,
          email,
          phone: cleanPhone,
          tags: tagsList,
          variations,
          assistantId
        });
      }

      if (phoneToRowData.size === 0) {
        alert('Nenhum contato válido encontrado no arquivo');
        setImporting(false);
        return;
      }

      const { data: existingContacts } = await supabase
        .from('account_contacts')
        .select('id, jid, name, phone')
        .eq('account_id', user.account_id)
        .in('jid', allPhoneVariations);

      const existingJidMap = new Map();
      if (existingContacts) {
        for (const contact of existingContacts) {
          existingJidMap.set(contact.jid, contact);
        }
      }

      const contactsToInsert = [];
      const contactsToUpdate = [];

      for (const [cleanPhone, rowData] of phoneToRowData.entries()) {
        let existingContact = null;

        for (const variation of rowData.variations) {
          if (existingJidMap.has(variation)) {
            existingContact = existingJidMap.get(variation);
            break;
          }
        }

        const canAssignToOthers =
          user?.can_assign_contacts === true || user?.type === 'agency_user';
        const importedAttributionId =
          !canAssignToOthers && user?.auth_user_id ? user.auth_user_id : null;

        const contactData = {
          account_id: user.account_id,
          name: rowData.nome,
          email: rowData.email,
          phone: rowData.phone,
          is_group: false,
          updated_at: new Date().toISOString(),
          tags: rowData.tags.length > 0 ? rowData.tags : null,
          account_assistant_id: rowData.assistantId || null,
          assistant_is_active: rowData.assistantId ? true : false
        };

        if (existingContact) {
          contactsToUpdate.push({
            id: existingContact.id,
            ...contactData
          });
        } else {
          const primaryJid = rowData.variations[0];
          contactsToInsert.push({
            jid: primaryJid,
            created_at: new Date().toISOString(),
            last_message_at: new Date().toISOString(),
            ...contactData,
            ...(importedAttributionId
              ? { attribution_id: importedAttributionId }
              : {})
          });
        }
      }

      let insertedCount = 0;
      let updatedCount = 0;

      if (contactsToInsert.length > 0) {
        const { data: insertedData, error: insertError } = await supabase
          .from('account_contacts')
          .insert(contactsToInsert)
          .select('id');

        if (insertError) {
          console.error('Erro ao inserir contatos:', insertError);
          alert(`Erro ao inserir contatos: ${insertError.message}`);
          setImporting(false);
          return;
        }

        insertedCount = insertedData?.length || 0;
      }

      if (contactsToUpdate.length > 0) {
        for (const contact of contactsToUpdate) {
          const { error: updateError } = await supabase
            .from('account_contacts')
            .update(contact)
            .eq('id', contact.id);

          if (updateError) {
            console.error('Erro ao atualizar contato:', updateError);
          } else {
            updatedCount++;
          }
        }
      }

      const totalCount = insertedCount + updatedCount;

      setImportedCount(totalCount);
      setImportSuccess(true);
      setFile(null);
      setPreviewData([]);

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error) {
      console.error('Erro na importação:', error);
      alert('Erro ao importar contatos. Tente novamente.');
    } finally {
      setImporting(false);
    }
  };

  const downloadTemplate = () => {
    const template = [
      ['nome', 'email', 'phone', 'tags', 'nome_do_agente'],
      ['João Silva', 'joao@example.com', '11999999999', 'cliente, vip', 'Assistente Vendas'],
      ['Maria Santos', 'maria@example.com', '11888888888', 'lead', 'Suporte Técnico'],
      ['Pedro Oliveira', '', '11777777777', 'cliente', '']
    ];

    const ws = XLSX.utils.aoa_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contatos');

    ws['!cols'] = [
      { wch: 20 },
      { wch: 25 },
      { wch: 15 },
      { wch: 30 },
      { wch: 25 }
    ];

    XLSX.writeFile(wb, 'modelo_importacao_contatos.xlsx');
  };

  return (
    <div className="flex h-[calc(100dvh-110px)] w-full flex-col text-[#0F172A] dark:text-[#FAFAFA]">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-[26px] font-semibold tracking-tight text-[#0F172A] sm:text-[30px] dark:text-[#FAFAFA]">
          Importar Contatos
        </h2>
        <Button
          onClick={() => window.history.back()}
          variant="ghost"
          className="text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A] dark:text-[#A1A1AA] dark:hover:bg-[#3F3F46] dark:hover:text-[#FAFAFA]"
        >
          <X className="mr-2 h-5 w-5" />
          Voltar
        </Button>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="mx-auto max-w-4xl space-y-6">
          {importSuccess && (
            <div className="flex items-start gap-3 rounded-xl border border-green-200 bg-green-50 p-4 dark:border-green-500/30 dark:bg-green-500/20">
              <CheckCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600 dark:text-green-500" />
              <div>
                <h3 className="mb-1 font-semibold text-green-800 dark:text-green-300">
                  Importação concluída!
                </h3>
                <p className="text-sm text-green-700 dark:text-green-200">
                  {importedCount} contato(s) importado(s) com sucesso.
                </p>
              </div>
            </div>
          )}

          <div className="rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-sm dark:border-[#3F3F464D] dark:bg-[#27272A] dark:shadow-none">
            <h3 className="mb-4 text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
              1. Baixar Modelo
            </h3>
            <p className="mb-4 text-sm text-[#64748B] dark:text-[#A1A1AA]">
              Faça o download do modelo Excel com as colunas corretas para importação.
            </p>
            <Button
              onClick={downloadTemplate}
              className="bg-[#2563EB] text-white hover:bg-[#1D4ED8]"
            >
              <Download className="mr-2 h-4 w-4" />
              Baixar Modelo
            </Button>
          </div>

          <div className="rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-sm dark:border-[#3F3F464D] dark:bg-[#27272A] dark:shadow-none">
            <h3 className="mb-4 text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
              2. Selecionar Arquivo
            </h3>
            <p className="mb-4 text-sm text-[#64748B] dark:text-[#A1A1AA]">
              Selecione o arquivo Excel (.xlsx ou .xls) preenchido com os dados dos contatos.
            </p>

            <div className="rounded-xl border-2 border-dashed border-[#E5E7EB] p-8 text-center transition-colors hover:border-[#2563EB]/50 dark:border-[#3F3F46] dark:hover:border-[#52525B]">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="hidden"
                id="file-upload"
              />
              <label
                htmlFor="file-upload"
                className="flex cursor-pointer flex-col items-center"
              >
                <FileSpreadsheet className="mb-3 h-12 w-12 text-[#94A3B8] dark:text-[#71717A]" />
                {file ? (
                  <div className="text-[#0F172A] dark:text-[#A1A1AA]">
                    <p className="font-semibold">{file.name}</p>
                    <p className="mt-1 text-sm text-[#64748B] dark:text-[#71717A]">
                      {(file.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                ) : (
                  <>
                    <p className="mb-1 font-medium text-[#0F172A] dark:text-[#A1A1AA]">
                      Clique para selecionar arquivo
                    </p>
                    <p className="text-sm text-[#64748B] dark:text-[#71717A]">
                      ou arraste e solte aqui
                    </p>
                  </>
                )}
              </label>
            </div>

            {validating && (
              <div className="mt-4 flex items-center justify-center gap-2 text-[#64748B] dark:text-[#A1A1AA]">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-[#64748B] border-t-transparent dark:border-[#A1A1AA]" />
                <span>Validando arquivo...</span>
              </div>
            )}

            {validationErrors.length > 0 && (
              <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-500/30 dark:bg-red-500/20">
                <div className="flex items-start gap-3">
                  <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-500" />
                  <div className="flex-1">
                    <h4 className="mb-2 font-semibold text-red-800 dark:text-red-300">
                      Erros de validação ({validationErrors.length})
                    </h4>
                    <div className="max-h-40 space-y-1 overflow-y-auto">
                      {validationErrors.slice(0, 10).map((error, index) => (
                        <p key={index} className="text-sm text-red-700 dark:text-red-200">
                          Linha {error.row} - {error.field}: {error.message}
                        </p>
                      ))}
                      {validationErrors.length > 10 && (
                        <p className="text-sm italic text-red-700 dark:text-red-200">
                          ... e mais {validationErrors.length - 10} erro(s)
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {previewData.length > 0 && validationErrors.length === 0 && (
              <div className="mt-4 rounded-xl border border-green-200 bg-green-50 p-4 dark:border-green-500/30 dark:bg-green-500/10">
                <h4 className="mb-3 font-semibold text-green-800 dark:text-green-300">
                  Preview (primeiros 5 registros)
                </h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-green-200 dark:border-[#3F3F46]">
                        <th className="pb-2 pr-4 text-left font-medium text-[#64748B] dark:text-[#A1A1AA]">
                          Nome
                        </th>
                        <th className="pb-2 pr-4 text-left font-medium text-[#64748B] dark:text-[#A1A1AA]">
                          E-mail
                        </th>
                        <th className="pb-2 pr-4 text-left font-medium text-[#64748B] dark:text-[#A1A1AA]">
                          Telefone
                        </th>
                        <th className="pb-2 pr-4 text-left font-medium text-[#64748B] dark:text-[#A1A1AA]">
                          Tags
                        </th>
                        <th className="pb-2 text-left font-medium text-[#64748B] dark:text-[#A1A1AA]">
                          Agente
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.map((contact, index) => (
                        <tr
                          key={index}
                          className="border-b border-green-100 last:border-0 dark:border-[#3F3F464D]"
                        >
                          <td className="py-2 pr-4 text-[#0F172A] dark:text-[#E4E4E7]">
                            {contact.nome}
                          </td>
                          <td className="py-2 pr-4 text-[#0F172A] dark:text-[#E4E4E7]">
                            {contact.email || '-'}
                          </td>
                          <td className="py-2 pr-4 text-[#0F172A] dark:text-[#E4E4E7]">
                            {contact.phone}
                          </td>
                          <td className="py-2 pr-4 text-[#0F172A] dark:text-[#E4E4E7]">
                            {contact.tags || '-'}
                          </td>
                          <td className="py-2 text-[#0F172A] dark:text-[#E4E4E7]">
                            {contact.nome_do_agente || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="rounded-xl border border-[#E5E7EB] bg-white p-6 shadow-sm dark:border-[#3F3F464D] dark:bg-[#27272A] dark:shadow-none">
            <h3 className="mb-4 text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
              3. Importar
            </h3>
            <p className="mb-4 text-sm text-[#64748B] dark:text-[#A1A1AA]">
              Após validar o arquivo, clique no botão abaixo para importar os contatos.
            </p>
            <Button
              onClick={handleImport}
              disabled={!file || validationErrors.length > 0 || importing || validating}
              className="bg-green-600 text-white hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {importing ? (
                <>
                  <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  Importando...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  Importar Contatos
                </>
              )}
            </Button>
          </div>

          <div className="rounded-xl border border-[#BFDBFE] bg-[#EFF6FF] p-4 dark:border-blue-500/30 dark:bg-blue-500/10">
            <h4 className="mb-2 font-semibold text-[#1E40AF] dark:text-blue-300">
              Informações importantes:
            </h4>
            <ul className="list-inside list-disc space-y-1 text-sm text-[#1E3A8A] dark:text-blue-200">
              <li>O arquivo deve estar no formato Excel (.xlsx ou .xls)</li>
              <li>As colunas obrigatórias são: nome, email, phone, tags</li>
              <li>
                A coluna <strong>nome_do_agente</strong> é opcional e deve conter o nome
                exato do assistente
              </li>
              <li>
                Se o nome do agente for fornecido e encontrado, o assistente será ativado
                automaticamente
              </li>
              <li>
                Contatos sem nome de agente ou com agente não encontrado serão importados
                sem agente associado
              </li>
              <li>Nomes de agentes não diferenciam maiúsculas de minúsculas</li>
              <li>O telefone deve conter apenas números (DDD + número)</li>
              <li>As tags devem ser separadas por vírgula</li>
              <li>Contatos duplicados serão atualizados</li>
              <li>A data de cadastro e atualização será registrada automaticamente</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportarContatos;
